import * as yup from 'yup';
import {
  combineRegex,
  isOldEnough,
  isYoungEnough,
  daysInMonth,
} from '@americanexpress/axp-field-validations-common';
import { alphaLower, alphaUpper, ampersand, apostrophe, hyphen, numbers, space, specialAlpha, specialCharacters } from '../regular-expressions';

export const emailRegex = /^[\w!#$%&'*+./=?^`{|}~-]+@[\w.-]+\.[\da-z]+$/i;

const createPrefixSchema = ({ errorMessagePrefix = '' } = {}) => {
  return yup.string()
    .required(`${errorMessagePrefix}prefix.required.error`)
    .oneOf(['MS', 'MR'], `${errorMessagePrefix}prefix.enum.error`);
};

const createTitleSchema = ({ errorMessagePrefix = '' } = {}) => {
  return yup.string()
    .notRequired()
    .oneOf(['', 'DOCTOR', 'PROFESSOR', 'PROFESSOR_DOCTOR'], `${errorMessagePrefix}title.enum.error`);
};

const createFirstNameSchema = ({ errorMessagePrefix = '' } = {}) => {
  return yup.string()
    .required(`${errorMessagePrefix}firstName.required.error`)
    .min(2, `${errorMessagePrefix}firstName.min.error`)
    .max(20, `${errorMessagePrefix}firstName.max.error`)
    .matches(combineRegex([alphaUpper, alphaLower, specialAlpha, apostrophe, space, hyphen]), `${errorMessagePrefix}firstName.regex.error`);
};

const createMiddleNameSchema = ({ errorMessagePrefix = '' } = {}) => {
  return yup.string()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .notRequired()
    .min(2, `${errorMessagePrefix}middleName.min.error`)
    .max(20, `${errorMessagePrefix}middleName.max.error`)
    .matches(combineRegex([alphaUpper, alphaLower, specialAlpha, apostrophe, space, hyphen]), `${errorMessagePrefix}middleName.regex.error`);
};

const createLastNameSchema = ({ errorMessagePrefix = '' } = {}) => {
  return yup.string()
    .required(`${errorMessagePrefix}lastName.required.error`)
    .min(2, `${errorMessagePrefix}lastName.min.error`)
    .max(20, `${errorMessagePrefix}lastName.max.error`)
    .matches(combineRegex([alphaUpper, alphaLower, specialAlpha, apostrophe, space, hyphen]), `${errorMessagePrefix}lastName.regex.error`);
};

const createNameOnCard = ({ errorMessagePrefix = '' } = {}) => {
  return yup.string()
    .required(`${errorMessagePrefix}nameOnCard.required.error`)
    .max(20, `${errorMessagePrefix}nameOnCard.max.error`)
    .matches(combineRegex([alphaUpper, numbers, specialCharacters, apostrophe, ampersand, space, hyphen]), `${errorMessagePrefix}nameOnCard.regex.error`);
};

const createDateOfBirth = ({ errorMessagePrefix = '' } = {}) => {
  return yup
    .string()
    .required(`${errorMessagePrefix}dateOfBirth.required.error`)
    .test('is-valid-length', `${errorMessagePrefix}dateOfBirth.length.error`, (value) => (value.replace(/\./g, '').length === 8))
    .matches(/^\d{2}\.\d{2}\.\d{4}/, `${errorMessagePrefix}dateOfBirth.format.error`)
    .test('is-month-valid', `${errorMessagePrefix}dateOfBirth.isValidMonth.error`, (values) => {
      // eslint-disable-next-line no-unused-vars
      const [day, month, year] = values.split('.');
      return Number.parseInt(month, 10) < 13 && Number.parseInt(month, 10) > 0;
    })
    .test('is-date', `${errorMessagePrefix}dateOfBirth.date.error`, (value) => {
      const [day, month, year] = value.split('.');
      if (month === '02') {
        return day <= daysInMonth['02'](year);
      }
      return day <= daysInMonth[month];
    })
    .test('is-old-enough', `${errorMessagePrefix}dateOfBirth.isOldEnough.error`, (value) => {
      const [day, month, year] = value.split('.');
      const transformedValue = `${year}-${month}-${day}`;

      return isOldEnough(transformedValue, 18, 'Europe/Berlin');
    })
    .test('is-young-enough', `${errorMessagePrefix}dateOfBirth.isYoungEnough.error`, (value) => {
      const [day, month, year] = value.split('.');
      const transformedValue = `${year}-${month}-${day}`;

      return isYoungEnough(transformedValue, 125, 'Europe/Berlin');
    });
};

const createEmailSchema = ({ errorMessagePrefix = '' } = {}) => {
  return yup.string()
    .required(`${errorMessagePrefix}email.required.error`)
    .min(5, `${errorMessagePrefix}email.min.error`)
    .max(150, `${errorMessagePrefix}email.max.error`)
    .email(`${errorMessagePrefix}email.isEmail.error`)
    .test('email-additional-check', `${errorMessagePrefix}email.isEmail.error`, (value) => emailRegex.test(value));
};

const prefix = createPrefixSchema();
const title = createTitleSchema();
const firstName = createFirstNameSchema();
const middleName = createMiddleNameSchema();
const lastName = createLastNameSchema();
const nameOnCard = createNameOnCard();
const dateOfBirth = createDateOfBirth();
const email = createEmailSchema();

const supplementary = {
  prefix: createPrefixSchema({ errorMessagePrefix: 'supplementary.' }),
  title: createTitleSchema({ errorMessagePrefix: 'supplementary.' }),
  firstName: createFirstNameSchema({ errorMessagePrefix: 'supplementary.' }),
  middleName: createMiddleNameSchema({ errorMessagePrefix: 'supplementary.' }),
  lastName: createLastNameSchema({ errorMessagePrefix: 'supplementary.' }),
  nameOnCard: createNameOnCard({ errorMessagePrefix: 'supplementary.' }),
  dateOfBirth: createDateOfBirth({ errorMessagePrefix: 'supplementary.' }),
  email: createEmailSchema({ errorMessagePrefix: 'supplementary.' }),
};

export {
  prefix,
  title,
  firstName,
  middleName,
  lastName,
  nameOnCard,
  dateOfBirth,
  email,
  supplementary,
};
